// Modal Component - Cosmic Glassmorphism Design
import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';

const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
};

const modalVariants = {
    hidden: {
        opacity: 0,
        scale: 0.9,
        y: 20
    },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: "spring",
            damping: 25,
            stiffness: 300
        }
    },
    exit: {
        opacity: 0,
        scale: 0.95,
        y: 10,
        transition: {
            duration: 0.2
        }
    }
};

const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    full: 'max-w-[90vw]'
};

export const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    isLoading = false,
    size = 'lg',
    showCloseButton = true,
    closeOnBackdrop = true,
    closeOnEscape = true
}) => {
    // Handle escape key
    const handleEscape = useCallback((e) => {
        if (e.key === 'Escape' && closeOnEscape) {
            onClose();
        }
    }, [onClose, closeOnEscape]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, handleEscape]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && closeOnBackdrop) {
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[300] flex items-center justify-center p-4"
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={backdropVariants}
                    transition={{ duration: 0.2 }}
                    onClick={handleBackdropClick}
                >
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

                    {/* Modal Content */}
                    <motion.div
                        className={`
                            relative w-full ${sizes[size]}
                            backdrop-blur-xl
                            rounded-2xl
                            shadow-2xl
                            overflow-hidden
                        `}
                        style={{
                            background: 'linear-gradient(135deg, rgba(17,17,35,0.95), var(--theme-glass-bg), rgba(17,17,35,0.95))',
                            border: '1px solid var(--theme-accent)',
                            boxShadow: '0 25px 50px -12px var(--theme-glow)',
                        }}
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Glow effect */}
                        <div
                            className="absolute -top-1 -left-1 -right-1 h-px"
                            style={{ background: 'linear-gradient(to right, transparent, var(--theme-accent), transparent)', opacity: 0.5 }}
                        />

                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--theme-glass-border)' }}>
                            <h2 className="text-xl font-semibold font-serif" style={{ color: 'var(--theme-text)' }}>
                                {title}
                            </h2>
                            {showCloseButton && (
                                <motion.button
                                    onClick={onClose}
                                    className="p-2 rounded-full transition-colors"
                                    style={{
                                        background: 'var(--theme-button-secondary-bg)',
                                        color: 'var(--theme-text)'
                                    }}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                >
                                    <X className="w-5 h-5" />
                                </motion.button>
                            )}
                        </div>

                        {/* Body */}
                        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto custom-scrollbar">
                            {isLoading ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <motion.div
                                        animate={{
                                            rotate: 360,
                                            scale: [1, 1.1, 1]
                                        }}
                                        transition={{
                                            rotate: { duration: 2, repeat: Infinity, ease: "linear" },
                                            scale: { duration: 1.5, repeat: Infinity }
                                        }}
                                    >
                                        <Sparkles className="w-12 h-12 text-amber-400" />
                                    </motion.div>
                                    <p className="mt-4 text-amber-200 text-lg">
                                        Consulting the cosmos...
                                    </p>
                                </div>
                            ) : (
                                children
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// Confirm Modal - for destructive actions
export const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'danger'
}) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[350] flex items-center justify-center p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

                    {/* Modal */}
                    <motion.div
                        className="relative w-full max-w-md backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden"
                        style={{
                            background: 'linear-gradient(135deg, rgba(17,17,35,0.95), var(--theme-glass-bg), rgba(17,17,35,0.95))',
                            border: '1px solid var(--theme-accent)',
                        }}
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.9, y: 20 }}
                    >
                        <div className="p-6">
                            <h3 className="text-xl font-semibold font-serif mb-3 text-center" style={{ color: 'var(--theme-text)' }}>
                                {title}
                            </h3>
                            <p className="text-center mb-6 whitespace-pre-line" style={{ color: 'var(--theme-text-secondary)' }}>
                                {message}
                            </p>
                            <div className="flex gap-4">
                                <motion.button
                                    onClick={onClose}
                                    className="flex-1 px-4 py-3 rounded-xl transition-colors"
                                    style={{
                                        background: 'var(--theme-button-secondary-bg)',
                                        border: '1px solid var(--theme-glass-border)',
                                        color: 'var(--theme-text)',
                                    }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {cancelText}
                                </motion.button>
                                <motion.button
                                    onClick={() => { onConfirm(); onClose(); }}
                                    className="flex-1 px-4 py-3 rounded-xl font-semibold transition-colors"
                                    style={{
                                        background: variant === 'danger'
                                            ? 'linear-gradient(135deg, var(--theme-error), #dc2626)'
                                            : 'var(--theme-button-primary)',
                                        color: variant === 'danger' ? 'white' : '#111',
                                    }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {confirmText}
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Modal;
